import React, { useCallback, useMemo, useRef } from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import reportApiFailure from 'shared/api/reportApiFailure'
import { useQueryClient } from '@tanstack/react-query'
import { HouseConsignment } from '../../../../house-consignment/models/types'
import useCreateHouseConsignment from '../../../../house-consignment/components/useCreateHouseConsignment'
import useDeleteHouseConsignment from '../../../../house-consignment/components/useDeleteHouseConsignment'
import EnsHouseConsignmentsTableRow from './EnsHouseConsignmentsTableRow'
import EnsHouseConsignmentForm from './EnsHouseConsignmentForm'
import EnsConsignmentItemTable from '../../consignment-item/components/EnsConsignmentItemTable'
import EnsConsignmentItemForm from '../../consignment-item/components/EnsConsignmentItemForm'
import { ConsignmentItemFormHandle } from '../../../transit/consignment-item/components/TransitConsignmentItemForm'
import ConsignmentItemDrawer from '../../../../consignment-item/components/ConsignmentItemDrawer'
import useConsignmentItemDrawer from '../../../../consignment-item/components/useConsignmentItemDrawer'
import HouseConsignmentDrawer from '../../../../house-consignment/components/HouseConsignmentDrawer'
import useHouseConsignmentDrawer from '../../../../house-consignment/components/useHouseConsignmentDrawer'
import useHouseConsignmentRowExpansion from '../../../../house-consignment/components/useHouseConsignmentRowExpansion'
import { belongsToHouseConsignment } from '../../../../house-consignment/components/nctsHouseConsignmentScope'
import { belongsToConsignmentItem, mapNctsErrorsToConsignmentItemFieldErrors } from '../../../../consignment-item/components/nctsConsignmentItemScope'
import { useNctsErrors } from '../../../contexts/NctsErrorContext'
import { QueryKeys } from '../../../../../key-registry'
import {
  HouseConsignmentFormHandle,
  mapHouseConsignmentToRowData,
} from '../../../../house-consignment/components/types'
import { useConfirm } from '../../../../../../shared/components/confirm/ConfirmProvider'
import LoadingOverlay from '../../../../../../components/p5/LoadingOverlay'
import useConsignmentItemsUpload from '../../../../consignment-item/components/useConsignmentItemsUpload'
import useDeclarationItemsTableActions from '../../../components/useDeclarationItemsTableActions'
import TransitHouseConsignmentsTableActions
  from '../../../../house-consignment/components/TransitHouseConsignmentsTableActions'
import useBulkDeleteHouseConsignments from '../../../../house-consignment/components/useBulkDeleteHouseConsignments'
import Checkbox from '../../../../../../components/p5/Checkbox'
import UploadIcon from '../../../../../../components/icons/UploadIcon'
import DownloadIcon from '../../../../../../components/icons/DownloadIcon'
import useToastError from '../../../../../../shared/components/toast-error/useToastError'

interface TransitHouseConsignmentsTableProps {
  consignmentId: number
  houseConsignments: HouseConsignment[]
  loading: boolean
}

function EnsHouseConsignmentsTable({
  consignmentId,
  houseConsignments,
  loading,
} : Readonly<TransitHouseConsignmentsTableProps>) {
  const { t } = useTranslation()
  const { confirm } = useConfirm()

  const formRef = useRef<HouseConsignmentFormHandle | null>(null)
  const consignmentItemFormRef = useRef<ConsignmentItemFormHandle | null>(null)
  const queryClient = useQueryClient()
  const rows = useMemo(
    () => houseConsignments.map(mapHouseConsignmentToRowData),
    [houseConsignments],
  )

  const {
    openHouseConsignmentId,
    openHouseConsignment,
    closeDrawer: closeHouseConsignmentDrawer,
    isDrawerOpen: isHouseConsignmentDrawerOpen,
  } = useHouseConsignmentDrawer()
  const openHouseConsignmentIndex = useMemo(
    () => rows.findIndex((r) => r.id === openHouseConsignmentId),
    [rows, openHouseConsignmentId],
  )
  const openHouseConsignmentData = openHouseConsignmentIndex >= 0 ? rows[openHouseConsignmentIndex] : null

  const {
    expandedHouseConsignmentId, toggleHouseConsignmentExpansion: rawToggleExpansion, isExpanded,
  } = useHouseConsignmentRowExpansion()
  const toggleHouseConsignmentExpansion = useCallback(async (id: number) => {
    if (expandedHouseConsignmentId != null) {
      await queryClient.invalidateQueries(QueryKeys.houseConsignment.all(consignmentId))
    }
    rawToggleExpansion(id)
  }, [consignmentId, expandedHouseConsignmentId, queryClient, rawToggleExpansion])

  const {
    openState: openConsignmentItemState,
    openConsignmentItem,
    closeDrawer: closeConsignmentItemDrawer,
    isDrawerOpen: isConsignmentItemDrawerOpen,
  } = useConsignmentItemDrawer()

  const { state: { errors: nctsErrors }, setNctsErrors } = useNctsErrors()
  const handleHouseConsignmentSaveSuccess = useCallback(() => {
    if (openHouseConsignmentIndex < 0) return
    setNctsErrors(nctsErrors.filter((err) => !belongsToHouseConsignment(err, openHouseConsignmentIndex)))
  }, [nctsErrors, openHouseConsignmentIndex, setNctsErrors])

  const consignmentItemFieldErrors = useMemo(() => (
    openConsignmentItemState
      ? mapNctsErrorsToConsignmentItemFieldErrors(
        nctsErrors,
        openConsignmentItemState.houseConsignmentIndex,
        openConsignmentItemState.consignmentItemIndex,
      )
      : []
  ), [nctsErrors, openConsignmentItemState])

  const handleConsignmentItemSaveSuccess = useCallback(() => {
    if (!openConsignmentItemState) return
    const { houseConsignmentIndex, consignmentItemIndex } = openConsignmentItemState
    setNctsErrors(nctsErrors.filter((err) => !belongsToConsignmentItem(err, houseConsignmentIndex, consignmentItemIndex)))
  }, [nctsErrors, openConsignmentItemState, setNctsErrors])

  const { notifyApiError } = useToastError()

  const { saveHouseConsignmentDraft } = useCreateHouseConsignment(consignmentId)
  const { deleteHouseConsignment } = useDeleteHouseConsignment(consignmentId)
  const { upload, isUploading } = useConsignmentItemsUpload(consignmentId)
  const { bulkDelete, isBulkDeleting } = useBulkDeleteHouseConsignments(consignmentId)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const hiddenAnchor = useRef<HTMLAnchorElement>(null)

  const rowIds = useMemo(() => rows.map((row) => row.id), [rows])

  const {
    selectedRowIds,
    selectedCount,
    allSelected,
    someSelected,
    deleteSelected,
    isBulkDeleteNotAllowed,
    clearSelection,
    toggleAllRowsSelected,
    toggleRowSelect,
  } = useDeclarationItemsTableActions({
    rowIds,
    onDeleteSelected: async (ids: number[]) => {
      const ok = await confirm({
        message: t('table.deleteHouseConsignmentsConfirmation', { count: ids.length }),
      })
      if (!ok) {
        return
      }

      try {
        await bulkDelete(Array.from(ids))
        toast.success(t('table.deletedHouseConsignments', { count: ids.length }))
      } catch (e) {
        reportApiFailure(e, () => notifyApiError(e))
      }
    },
    isInlineEditActive: isHouseConsignmentDrawerOpen || isConsignmentItemDrawerOpen,
    hasUnsavedChanges: false,
    isBulkDeleting,
  })

  const addRow = async () => {
    try {
      const newHouseConsignment = await saveHouseConsignmentDraft(rows.length)
      if (!newHouseConsignment) {
        toast.error(t('houseConsignment.createError'))
        return
      }

      toast.success(t('houseConsignment.createSuccess'))
    } catch (e) {
      reportApiFailure(e, () => toast.error(t('houseConsignment.createError')))
    }
  }

  const removeRow = async (id: number) => {
    const ok = await confirm({
      message: t('table.deleteHouseConsignmentsConfirmation', { count: 1 }),
    })
    if (!ok) {
      return
    }

    try {
      await deleteHouseConsignment(id)
      toast.success(t('table.deletedHouseConsignments', { count: 1 }))
    } catch (e) {
      reportApiFailure(e, () => notifyApiError(e))
    }
  }

  const uploadFile = async (file: File) => {
    try {
      const uploadResult = await upload(file)
      if (uploadResult.errors?.length) {
        toast.warn(t('consignmentItemImport.import', { context: 'incomplete' }))
        return
      }

      toast.success(t('consignmentItemImport.import', { context: 'success' }))
    } catch (error) {
      reportApiFailure(error, () => notifyApiError(error))
    }
  }

  const handleUpload = () => {
    fileInputRef.current?.click()
  }

  const downloadExample = () => {
    hiddenAnchor.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      uploadFile(file)
    } finally {
      if (fileInputRef.current) { fileInputRef.current.value = '' }
    }
  }

  const isAddButtonDisabled = rows.length > 1999 || isConsignmentItemDrawerOpen
  const isHouseConsignmentEditDisabled = selectedCount > 0 || isConsignmentItemDrawerOpen
  const isExpansionToggleDisabled = selectedCount > 0 || isConsignmentItemDrawerOpen

  return (
    <LoadingOverlay loading={loading}>
      {selectedCount > 0 && (
        <div className="mb-3">
          <TransitHouseConsignmentsTableActions
            selectedCount={selectedCount}
            rows={rows}
            onClearSelection={clearSelection}
            onDeleteSelected={deleteSelected}
            isBulkDeleteNotAllowed={isBulkDeleteNotAllowed}
            isBulkDeleting={isBulkDeleting}
          />
        </div>
      )}
      <div className="row">
        <div className="col-12 d-flex justify-content-end align-items-center gap-2 pb-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleUpload}
            disabled={isUploading}
          >
            <input
              style={{ display: 'none' }}
              accept=".csv,text/csv"
              ref={fileInputRef}
              onChange={handleFileChange}
              type="file"
              disabled={isUploading}
            />
            <UploadIcon />
            <span>
              {t('excel.importGoods')}
            </span>
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={downloadExample}
          >
            <a
              className="d-none"
              ref={hiddenAnchor}
              target="_blank"
              href={`${process.env.PUBLIC_URL}/consignmentItems.csv`}
              download="consignmentItems.csv"
              rel="noreferrer"
            >
              {t('consignmentItemImport.example')}
            </a>
            <DownloadIcon />
            <span>
              {t('consignmentItemImport.example')}
            </span>
          </Button>
          <Button
            className="btn btn-sm btn-primary"
            onClick={() => addRow()}
            disabled={isAddButtonDisabled}
          >
            <i className="fa fa-plus me-2" />
            {t('declaration.p5.addHouseConsignment')}
          </Button>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="table-responsive">
            <table className="consignment-table">
              <thead>
                <tr>
                  <th className="w-1 whitespace-nowrap align-middle">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={!allSelected && someSelected}
                      onChange={toggleAllRowsSelected}
                      wrapperClassName="d-flex align-items-center justify-content-center"
                      inputClassName="form-check-input m-0 float-none"
                    />
                  </th>
                  <th>{t('declaration.p5.sequenceNumber')}</th>
                  <th>{t('declaration.p5.seller')}</th>
                  <th>{`${t('declaration.p5.seller')} ${t('declaration.p5.country')}`}</th>
                  <th>{t('declaration.p5.buyer')}</th>
                  <th>{`${t('declaration.p5.buyer')} ${t('declaration.p5.country')}`}</th>
                  <th>{t('declaration.numberOfGoods')}</th>
                  <th className="col-1">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((rowData, index) => (
                  <React.Fragment key={rowData.id}>
                    <EnsHouseConsignmentsTableRow
                      houseConsignment={rowData}
                      houseConsignmentIndex={index}
                      onToggleConsignmentItems={() => toggleHouseConsignmentExpansion(rowData.id)}
                      onRowEdit={() => openHouseConsignment(rowData.id)}
                      onRowRemove={() => removeRow(rowData.id)}
                      onRowSelect={() => toggleRowSelect(rowData.id)}
                      selected={selectedRowIds.has(rowData.id)}
                      isExpanded={isExpanded(rowData.id)}
                      editDisabled={isHouseConsignmentEditDisabled}
                      consignmentItemsToggleDisabled={isExpansionToggleDisabled}
                    />
                    {isExpanded(rowData.id) && (
                      <tr className="hc-row-goods">
                        <td colSpan={8} className="py-3">
                          <div className="hc-row-goods__scroll">
                            <EnsConsignmentItemTable
                              consignmentId={consignmentId}
                              houseConsignmentId={rowData.id}
                              onOpenConsignmentItem={(consignmentItemId, consignmentItemIndex) => openConsignmentItem({
                                consignmentItemId,
                                consignmentItemIndex,
                                houseConsignmentId: rowData.id,
                                houseConsignmentIndex: index,
                              })}
                              editDisabled={isHouseConsignmentDrawerOpen}
                              isConsignmentItemDrawerOpen={isConsignmentItemDrawerOpen}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <HouseConsignmentDrawer
        isOpen={isHouseConsignmentDrawerOpen}
        houseConsignmentSequenceNumber={openHouseConsignmentIndex + 1}
        formRef={formRef}
        onClose={closeHouseConsignmentDrawer}
      >
        {openHouseConsignmentData && (
          <EnsHouseConsignmentForm
            rowData={openHouseConsignmentData}
            onSaveSuccess={handleHouseConsignmentSaveSuccess}
            ref={formRef}
          />
        )}
      </HouseConsignmentDrawer>
      <ConsignmentItemDrawer
        isOpen={isConsignmentItemDrawerOpen}
        houseConsignmentSequenceNumber={(openConsignmentItemState?.houseConsignmentIndex ?? -1) + 1}
        consignmentItemSequenceNumber={(openConsignmentItemState?.consignmentItemIndex ?? -1) + 1}
        formRef={consignmentItemFormRef}
        onClose={closeConsignmentItemDrawer}
      >
        {openConsignmentItemState && (
          <EnsConsignmentItemForm
            id={openConsignmentItemState.consignmentItemId}
            houseConsignmentId={openConsignmentItemState.houseConsignmentId}
            onSaveSuccess={handleConsignmentItemSaveSuccess}
            fieldErrors={consignmentItemFieldErrors}
            ref={consignmentItemFormRef}
          />
        )}
      </ConsignmentItemDrawer>
    </LoadingOverlay>
  )
}

export default EnsHouseConsignmentsTable
